import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import Icon from './Icon';
import {
  buildMediatorOrderMessage,
  buildMediatorAnnouncementMessage,
  shareWhatsAppMessage,
  formatFeedbackLabel,
} from '../../utils/whatsappMessage';
import { fetchRefundPolicy, DEFAULT_REFUND_POLICY } from '../../utils/refundPolicy';
import toast from 'react-hot-toast';

const WhatsAppMessageModal = ({
  isOpen,
  onClose,
  product,
  feedbacks = [],
  orderFormLink = '',
  messageType = 'order',
  mediatorName = '',
}) => {
  const defaultFeedback = feedbacks[0] || {};
  const isOrderMessage = messageType === 'order';
  const [buyerPayoutMode, setBuyerPayoutMode] = useState('LESS');
  const [buyerPayoutAmount, setBuyerPayoutAmount] = useState('');
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [refundPolicy, setRefundPolicy] = useState(DEFAULT_REFUND_POLICY);

  useEffect(() => {
    if (!isOpen) return;
    if (isOrderMessage) {
      fetchRefundPolicy().then(setRefundPolicy);
    }
  }, [isOpen, isOrderMessage]);

  useEffect(() => {
    if (!isOpen || !product) return;
    setBuyerPayoutMode('LESS');
    setBuyerPayoutAmount('');
    setGeneratedMessage('');
  }, [isOpen, product, feedbacks, messageType]);

  const buildMessage = () => {
    if (isOrderMessage) {
      return buildMediatorOrderMessage({
        product,
        feedbacks,
        payoutMode: buyerPayoutMode,
        payoutAmount: buyerPayoutAmount,
        orderFormLink,
        mediatorName: mediatorName || product.mediatorName,
        policyInput: refundPolicy,
      });
    }
    return buildMediatorAnnouncementMessage({
      product,
      feedbacks,
      payoutMode: buyerPayoutMode,
      payoutAmount: buyerPayoutAmount,
    });
  };

  const handleGenerate = () => {
    setGeneratedMessage(buildMessage());
  };

  const handleCopy = async () => {
    if (!generatedMessage) return;
    try {
      await navigator.clipboard.writeText(generatedMessage);
      toast.success('Message copied!');
    } catch (error) {
      toast.error('Failed to copy message');
    }
  };

  const handleShare = () => {
    const message = generatedMessage || buildMessage();
    shareWhatsAppMessage(message);
  };

  const modalTitle = isOrderMessage ? 'Create Order Message' : 'Create Deal Announcement';
  const amountLabel = buyerPayoutMode === 'LESS'
    ? 'Less amount for buyer (₹)'
    : 'Commission amount for buyer (₹)';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} width={580}>
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {product && (
          <div style={{ padding: '12px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '13px' }}>
            <div style={{ fontWeight: 700, color: '#1e293b' }}>{product.productName}</div>
            <div style={{ marginTop: '6px', color: '#64748b' }}>
              {feedbacks.map((fb) => fb.feedbackType).filter(Boolean).join(' + ') || 'Deal'}
            </div>
            {product.productCode && (
              <div style={{ marginTop: '6px', color: '#64748b' }}>Product Code: {product.productCode}</div>
            )}
          </div>
        )}

        {isOrderMessage && feedbacks.length > 0 && (
          <div style={{ padding: '12px', borderRadius: '10px', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
              Admin-set payout (reference only)
            </div>
            {feedbacks.map((feedback) => (
              <div key={feedback.feedbackType} style={{ fontSize: '13px', color: '#475569', marginBottom: '4px' }}>
                {formatFeedbackLabel(feedback.feedbackType)}: {feedback.payoutType || 'LESS'} · ₹{feedback.payoutValue ?? 0}
                {feedback.remainingSlots != null && ` · ${feedback.remainingSlots} slots left`}
              </div>
            ))}
          </div>
        )}

        {isOrderMessage && orderFormLink && (
          <div style={{ padding: '10px 12px', borderRadius: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '12px', color: '#1d4ed8', wordBreak: 'break-all' }}>
            Includes order form link, refund form link, slots, ASIN, and terms
          </div>
        )}

        {!isOrderMessage && (
          <div style={{ padding: '10px 12px', borderRadius: '8px', background: '#fef3c7', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>
            Announcement message has no portal links — use for public deal posts (DM FOR LINK)
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
            Show buyers as
          </label>
          <div style={{ display: 'flex', gap: '10px' }}>
            {['LESS', 'COMMISSION'].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setBuyerPayoutMode(mode)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: buyerPayoutMode === mode ? '2px solid #059669' : '1px solid #e2e8f0',
                  background: buyerPayoutMode === mode ? '#ecfdf5' : '#fff',
                  color: buyerPayoutMode === mode ? '#059669' : '#475569',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
            {amountLabel} <span style={{ fontWeight: 400, color: '#64748b' }}>(optional — leave blank and type in message below)</span>
          </label>
          <input
            type="number"
            min="0"
            value={buyerPayoutAmount}
            onChange={(e) => setBuyerPayoutAmount(e.target.value)}
            placeholder="Leave empty — fill less/commission directly in the message text"
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px' }}
          />
          <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b' }}>
            Less price is never taken from admin settings. Generate the message, then type the amount in the editable box (e.g. change <code>Review - Less: /-</code> to <code>Review - Less: 169/-</code>).
          </p>
        </div>

        <Button onClick={handleGenerate}>Generate Message</Button>

        {generatedMessage && (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
              Message (editable)
            </label>
            <textarea
              value={generatedMessage}
              onChange={(e) => setGeneratedMessage(e.target.value)}
              rows={isOrderMessage ? 18 : 12}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', lineHeight: 1.5, background: '#fff', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <Button onClick={handleCopy} variant="secondary" style={{ flex: 1 }}>
                <Icon name="copy" size={16} /> Copy
              </Button>
              <Button onClick={handleShare} style={{ flex: 1, background: '#25D366' }}>
                <Icon name="wa" size={16} color="white" /> Share on WhatsApp
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WhatsAppMessageModal;
